// Accueil par profil (charte D-038, « exceptions d'abord ») : situation nationale ou du périmètre pour la
// supervision et l'administration, réseau du distributeur, contrôles de l'inspecteur, installation de
// l'opérateur, flotte du transporteur, fiche de sa station (gérant), état des services (support technique),
// page publique. Les points d'attention sont les alertes ouvertes du périmètre (moteur d'alertes du lot 2) ;
// la carte reçoit la situation calculée par le serveur (suffisance de l'information et tension par province).

import { useState } from 'react';
import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { CATEGORIES_INSTALLATIONS, formaterNombre } from '@carburanttrack/shared';
import { api } from '../../api/client';
import type { InstallationListe, Situation } from '../../api/types';
import { useCarte } from '../../api/requetes';
import { useFiltres } from '../../app/filtres';
import { useSession } from '../../app/session';
import { CarteProvinces, type ModeCarte } from '../../composants/CarteProvinces';
import { Indisponible, MessageErreur, Squelette } from '../../composants/Etats';
import { libelleCategorie, libelleProvince } from '../../composants/format';
import { Jauge } from '../../composants/Visualisations';
import { AccueilTechnique } from './AccueilTechnique';
import { AccueilPublic } from './AccueilPublic';
import { AccueilOperateur } from './AccueilRoles';
import { inscription as inscriptionInspections } from '../inspections';
import { AccueilMissions, BlocLivraisonsEnCours, PageAccueilStation } from '../logistique';
import { comptesReferentiel, ListePointsAttention, pluriel, PointsAlertes, QualiteInformation, useLigneContexte, usePointsReferentiel, useSituation } from './communs';

export function Accueil() {
  const { etat } = useSession();
  switch (etat?.profil) {
    case 'technique':
      return <AccueilTechnique />;
    case 'citoyen':
      return <AccueilPublic />;
    case 'station':
      return <PageAccueilStation />;
    case 'inspecteur':
      // Accueil de l'inspecteur (artboard P04) livré par le module des inspections (G2).
      return inscriptionInspections.accueil.element;
    case 'operateur':
      return <AccueilOperateur />;
    case 'conducteur':
      return <AccueilMissions />;
    case 'distributeur':
      return <AccueilReseau variante="distributeur" />;
    default:
      return <AccueilReseau variante="supervision" />;
  }
}

/** Situation du réseau : colonne des exceptions à gauche, carte et compteurs à droite (Reseau, T01). */
function AccueilReseau({ variante }: { variante: 'supervision' | 'distributeur' }) {
  const { etat } = useSession();
  const { province, definirProvince } = useFiltres();
  const situation = useSituation();
  const carte = useCarte();
  const [mode, setMode] = useState<ModeCarte>('exceptions');
  const contexte = useLigneContexte();
  const points = useQuery({
    queryKey: ['installations', 'carte'],
    queryFn: async () => (await api<InstallationListe[]>('/api/v1/installations?limite=200')).donnees,
    enabled: situation.data?.installationsLisibles === true,
  });
  const pointsAttention = usePointsReferentiel(situation.data);

  if (situation.isLoading) return <Squelette lignes={8} libelle="Chargement de la situation" />;
  if (situation.error) return <MessageErreur erreur={situation.error} />;
  const s = situation.data!;
  const c = comptesReferentiel(s, province);
  const perimetreProvinces = [...new Set(etat?.affectations?.flatMap((a) => a.perimetre.provinces) ?? [])];
  const titre =
    variante === 'distributeur'
      ? 'Mon réseau'
      : province
        ? `Situation, ${libelleProvince(province)}`
        : perimetreProvinces.length > 0
          ? `Situation, ${perimetreProvinces.map(libelleProvince).join(', ')}`
          : 'Situation nationale';
  const intro =
    variante === 'distributeur'
      ? `${pluriel(c.stations, 'station')} et ${pluriel(c.depots, 'dépôt')} dans votre réseau. Les installations normales restent discrètes, seuls les points à traiter sont en couleur.`
      : mode === 'exceptions'
        ? 'Tout ce qui est normal reste discret. La couleur est réservée à ce qui demande une action.'
        : 'Toutes les provinces sont colorées selon leur état.';
  const pointsAffiches = (points.data ?? []).filter((p) => !province || p.provinceCode === province);

  const colonneExceptions = (
    <section className="carte-bloc colonne-exceptions" aria-labelledby="titre-situation">
      <div className="tete-situation">
        <span className="contexte">{contexte}</span>
        <h1 id="titre-situation">{titre}</h1>
        <p className="attenue">{intro}</p>
      </div>
      <section aria-labelledby="titre-alertes">
        <h2 id="titre-alertes" className="sur-titre">
          Points d’attention
        </h2>
        <PointsAlertes province={province} />
      </section>
      {pointsAttention.length > 0 && (
        <section aria-labelledby="titre-a-traiter">
          <h2 id="titre-a-traiter" className="sur-titre">
            À traiter dans le référentiel
          </h2>
          <ListePointsAttention points={pointsAttention} />
        </section>
      )}
      {s.installationsLisibles ? <QualiteInformation situation={s} province={province} /> : <Indisponible motif={s.motifIndisponible ?? 'accès non autorisé'} />}
    </section>
  );

  const compteurs = s.installationsLisibles && (
    <div className="compteurs joints" role="list" aria-label="Compteurs du référentiel">
      <div role="listitem">
        Installations<b>{formaterNombre(c.total)}</b>
      </div>
      <div role="listitem">
        Validées<b>{formaterNombre(c.valides)}</b>
      </div>
      <Link role="listitem" to="/referentiel/installations?etat=en_attente_validation">
        En attente de validation<b className={c.enAttente > 0 ? 'ambre' : undefined}>{formaterNombre(c.enAttente)}</b>
      </Link>
      <div role="listitem">
        Stations-service<b>{formaterNombre(c.stations)}</b>
      </div>
      <div role="listitem">
        {variante === 'distributeur' ? 'Dépôts' : 'Dépôts et centres'}
        <b>{formaterNombre(variante === 'distributeur' ? c.depots : c.depotsEtCentres)}</b>
      </div>
    </div>
  );

  const blocCarte = (
    <section className="carte-bloc" aria-labelledby="titre-carte">
      <div className="carte-bloc-tete">
        <h2 id="titre-carte">{variante === 'distributeur' ? 'Vos installations sur le territoire' : 'Carte des provinces'}</h2>
        {variante === 'supervision' && (
          <div className="segments" role="radiogroup" aria-label="Mode d’affichage de la carte">
            <button type="button" role="radio" aria-checked={mode === 'exceptions'} onClick={() => setMode('exceptions')}>
              Exceptions
            </button>
            <button type="button" role="radio" aria-checked={mode === 'tout'} onClick={() => setMode('tout')}>
              Tout afficher
            </button>
          </div>
        )}
      </div>
      {carte.isLoading && <Squelette lignes={6} libelle="Chargement de la carte" />}
      {carte.error && <MessageErreur erreur={carte.error} titre="Carte indisponible" />}
      {carte.data && (
        <CarteProvinces
          carte={carte.data}
          situations={s.provinces}
          points={pointsAffiches}
          selection={province}
          surSelection={definirProvince}
          mode={mode}
          titre="Carte des neuf provinces du Gabon ; sélectionnez une province pour filtrer"
        />
      )}
      <p className="attenue note-carte">
        Les missions en cours et leur dernière position sont sur la carte de la page <Link to="/livraisons">Livraisons</Link>.
      </p>
    </section>
  );

  if (variante === 'distributeur') {
    return (
      <div className="accueil-reseau grille-accueil">
        {colonneExceptions}
        <div className="colonne-carte">
          {compteurs}
          <div className="grille-2">
            {blocCarte}
            <div>
              <BlocLivraisonsEnCours />
              {s.installationsLisibles && <BlocCategories situation={s} province={province} />}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="accueil-reseau grille-accueil">
      {colonneExceptions}
      <div className="colonne-carte">
        {blocCarte}
        {compteurs}
        <div className="grille-2">
          <BlocTerritoire situation={s} province={province} />
          {s.installationsLisibles && <BlocCategories situation={s} province={province} />}
        </div>
      </div>
    </div>
  );
}

function BlocCategories({ situation, province }: { situation: Situation; province: string | null }) {
  const c = comptesReferentiel(situation, province);
  const parCategorie = CATEGORIES_INSTALLATIONS.map((cat) => ({ cat, n: c.parCategorie(cat) })).filter((x) => x.n > 0);
  return (
    <section className="carte-bloc" aria-labelledby="titre-categories">
      <h2 id="titre-categories">
        Installations <small>par catégorie</small>
      </h2>
      {parCategorie.length === 0 && <p className="attenue">Aucune installation dans ce territoire.</p>}
      {parCategorie.map(({ cat, n }) => (
        <Jauge key={cat} libelle={libelleCategorie(cat)} part={c.total > 0 ? (n / c.total) * 100 : null} valeur={formaterNombre(n)} sousValeur={`sur ${formaterNombre(c.total)}`} motifIndisponible="aucune installation" />
      ))}
    </section>
  );
}

const LIBELLES_TENSION = { nulle: 'aucune', vigilance: 'vigilance', difficulte: 'difficulté' } as const;

function BlocTerritoire({ situation, province }: { situation: Situation; province: string | null }) {
  const p = province ? situation.provinces.find((x) => x.code === province) : null;
  return (
    <section className="carte-bloc" aria-labelledby="titre-territoire">
      <h2 id="titre-territoire">{p ? p.nom : 'Territoire'}</h2>
      {p ? (
        <dl className="paires">
          <div>
            <dt>Chef-lieu</dt>
            <dd>{p.chefLieu}</dd>
          </div>
          <div>
            <dt>Information</dt>
            <dd>{p.information === 'insuffisante' ? 'insuffisante' : 'suffisante'}</dd>
          </div>
          <div>
            <dt>Données récentes</dt>
            <dd>{p.motifInformation}</dd>
          </div>
          <div>
            <dt>Tension</dt>
            <dd>{p.tension ? LIBELLES_TENSION[p.tension] : <Indisponible motif={p.motifTension ?? 'non évaluée tant que l’information est insuffisante'} />}</dd>
          </div>
          <div>
            <dt>Alertes ouvertes</dt>
            <dd>{p.alertesOuvertes !== null ? formaterNombre(p.alertesOuvertes) : <Indisponible motif={p.motifTension ?? 'alertes non lisibles'} />}</dd>
          </div>
        </dl>
      ) : (
        <p className="attenue">Sélectionnez une province sur la carte ou dans le filtre « Territoire » pour en afficher le détail.</p>
      )}
    </section>
  );
}
